import { useCallback, useEffect, useRef, useState } from 'react';
const clearTimer = (timer)=>{
    if (timer.current) {
        clearTimeout(timer.current);
        timer.current = undefined;
    }
};
/**
 * Interaction state machine for {@link Tooltip}.
 *
 * Touch vs pointer: on touch devices we attach **no hover/focus listeners** (so the
 * emulated `mouseover` a tap produces can't re-open a just-closed tooltip — the old
 * MUI Tooltip's mobile-persistence bug) and toggle on the synthesized **click**
 * instead. Relying on `click` lets the browser's own tap detection absorb the finger
 * jitter that manual `touchmove` tracking would mistake for a drag (taps silently
 * dropped on Android Chrome). Dismissal is delegated to a `ClickAwayListener` plus
 * Escape and (touch) scroll. Nothing auto-hides on a timer, so a tapped tooltip can be
 * read at leisure (WCAG 1.4.13). `enterDelay`/`leaveDelay` apply to hover only.
 *
 * Controlled vs uncontrolled: when `open` is a boolean the parent owns visibility and
 * internal state never flips, but enabled listeners still fire `onOpen`/`onClose` so
 * controlled consumers (`infoIcon`, `fabHelpButton`) keep working. When every listener
 * is disabled (the `menuCell` persistent tooltip) no handlers/timers are attached at all.
 */ export const useTooltip = ({ open, onOpen, onClose, enterDelay, leaveDelay, disableHoverListener, disableFocusListener, disableTouchListener, disableInteractive, isTouchDevice })=>{
    const isControlled = open !== undefined;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const isOpen = isControlled ? open : uncontrolledOpen;
    const [anchorEl, setAnchorEl] = useState(null);
    const enterTimer = useRef(undefined);
    const leaveTimer = useRef(undefined);
    // Latest values, read inside delayed callbacks to avoid stale closures.
    const latest = useRef({
        isControlled,
        onOpen,
        onClose,
        isOpen
    });
    latest.current = {
        isControlled,
        onOpen,
        onClose,
        isOpen
    };
    const fireOpen = useCallback((event)=>{
        latest.current.onOpen?.(event);
        if (!latest.current.isControlled) {
            setUncontrolledOpen(true);
        }
    }, []);
    const fireClose = useCallback((event)=>{
        latest.current.onClose?.(event);
        if (!latest.current.isControlled) {
            setUncontrolledOpen(false);
        }
    }, []);
    const scheduleOpen = (event)=>{
        clearTimer(leaveTimer);
        clearTimer(enterTimer);
        if (enterDelay > 0) {
            enterTimer.current = setTimeout(()=>fireOpen(event), enterDelay);
        } else {
            fireOpen(event);
        }
    };
    const scheduleClose = (event)=>{
        clearTimer(enterTimer);
        clearTimer(leaveTimer);
        if (leaveDelay > 0) {
            leaveTimer.current = setTimeout(()=>fireClose(event), leaveDelay);
        } else {
            fireClose(event);
        }
    };
    // Primary anchor source: the child's ref. Stable so React doesn't re-attach each render.
    const setAnchorRef = useCallback((node)=>{
        setAnchorEl(node);
    }, []);
    // Fallback anchor source: the element that received an open-intent event. Only fills
    // when the ref didn't (e.g. children that don't forward refs, like `NumberBadge`).
    const captureAnchor = (element)=>{
        if (element) {
            setAnchorEl((previous)=>previous ?? element);
        }
    };
    const anchorListeners = {};
    if (!isTouchDevice && !disableHoverListener) {
        anchorListeners.onMouseEnter = (event)=>{
            captureAnchor(event.currentTarget);
            scheduleOpen(event);
        };
        anchorListeners.onMouseLeave = (event)=>{
            scheduleClose(event);
        };
    }
    if (!isTouchDevice && !disableFocusListener) {
        anchorListeners.onFocus = (event)=>{
            // Only open for keyboard focus, not the focus a mouse/touch click leaves behind.
            const target = event.target;
            try {
                if (typeof target.matches === 'function' && !target.matches(':focus-visible')) {
                    return;
                }
            } catch  {
            // jsdom can't evaluate :focus-visible — fall through and open.
            }
            captureAnchor(event.currentTarget);
            scheduleOpen(event);
        };
        anchorListeners.onBlur = (event)=>{
            scheduleClose(event);
        };
    }
    if (isTouchDevice && !disableTouchListener) {
        // Toggle on the synthesized click — the browser's tap detection tolerates the
        // finger jitter that manual touchstart/touchmove tracking misreads as a drag.
        anchorListeners.onClick = (event)=>{
            captureAnchor(event.currentTarget);
            if (latest.current.isOpen) {
                fireClose(event);
            } else {
                fireOpen(event);
            }
        };
    }
    const popperListeners = {};
    if (!isTouchDevice && !disableInteractive && !disableHoverListener) {
        popperListeners.onMouseEnter = ()=>{
            // Pointer moved from anchor onto the popup — cancel the pending close.
            clearTimer(leaveTimer);
        };
        popperListeners.onMouseLeave = (event)=>{
            scheduleClose(event);
        };
    }
    const handleClickAway = (event)=>{
        if (!latest.current.isOpen) {
            return;
        }
        const target = event.target;
        // A press on the anchor toggles via its own onClick — don't also close here.
        if (anchorEl && target && anchorEl.contains(target)) {
            return;
        }
        fireClose(event);
    };
    // Escape (everywhere) and scroll (touch) dismissal while open. Outside-press is
    // handled declaratively by the ClickAwayListener in the component.
    useEffect(()=>{
        if (!isOpen) {
            return undefined;
        }
        const onKeyDown = (event)=>{
            if (event.key === 'Escape') {
                fireClose(event);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        let onScroll;
        if (isTouchDevice) {
            onScroll = (event)=>fireClose(event);
            window.addEventListener('scroll', onScroll, {
                capture: true,
                passive: true
            });
        }
        return ()=>{
            document.removeEventListener('keydown', onKeyDown);
            if (onScroll) {
                window.removeEventListener('scroll', onScroll, {
                    capture: true
                });
            }
        };
    }, [
        isOpen,
        isTouchDevice,
        fireClose
    ]);
    // Never leave a timer running past unmount (the persistent `menuCell` tooltip
    // mounts/unmounts frequently).
    useEffect(()=>()=>{
            clearTimer(enterTimer);
            clearTimer(leaveTimer);
        }, []);
    return {
        isOpen,
        anchorEl,
        setAnchorRef,
        anchorListeners,
        popperListeners,
        handleClickAway
    };
};

//# sourceMappingURL=useTooltip.js.map